import { __decorate } from 'tslib';
import { ReactWrapperComponent, AngularReact, registerElement } from '@angular-react/core';
import * as i0 from '@angular/core';
import { Directive, Input, EventEmitter, Component, ChangeDetectionStrategy, ViewChild, Output, ContentChild, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { Styled } from '@angular-react/fabric/lib/utils';
import { CommonModule } from '@angular/common';
import { Calendar } from '@fluentui/react/lib/Calendar';

// Copyright (c) Microsoft Corporation. All rights reserved.
/**
 * Wrapper directive for calendar strings
 */
class CalendarStringsDirective {
    get strings() {
        return {
            months: this.months,
            shortMonths: this.shortMonths,
            days: this.days,
            shortDays: this.shortDays,
            goToToday: this.goToToday,
            prevMonthAriaLabel: this.prevMonthAriaLabel,
            nextMonthAriaLabel: this.nextMonthAriaLabel,
            prevYearAriaLabel: this.prevYearAriaLabel,
            nextYearAriaLabel: this.nextYearAriaLabel,
            prevYearRangeAriaLabel: this.prevYearRangeAriaLabel,
            nextYearRangeAriaLabel: this.nextYearRangeAriaLabel,
            closeButtonAriaLabel: this.closeButtonAriaLabel,
            weekNumberFormatString: this.weekNumberFormatString
        };
    }
}
/** @nocollapse */ CalendarStringsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: CalendarStringsDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ CalendarStringsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: CalendarStringsDirective, selector: "fab-calendar > fab-calendar-strings", inputs: { months: "months", shortMonths: "shortMonths", days: "days", shortDays: "shortDays", goToToday: "goToToday", prevMonthAriaLabel: "prevMonthAriaLabel", nextMonthAriaLabel: "nextMonthAriaLabel", prevYearAriaLabel: "prevYearAriaLabel", nextYearAriaLabel: "nextYearAriaLabel", prevYearRangeAriaLabel: "prevYearRangeAriaLabel", nextYearRangeAriaLabel: "nextYearRangeAriaLabel", closeButtonAriaLabel: "closeButtonAriaLabel", weekNumberFormatString: "weekNumberFormatString" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: CalendarStringsDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'fab-calendar > fab-calendar-strings' }]
        }], propDecorators: { months: [{
                type: Input
            }], shortMonths: [{
                type: Input
            }], days: [{
                type: Input
            }], shortDays: [{
                type: Input
            }], goToToday: [{
                type: Input
            }], prevMonthAriaLabel: [{
                type: Input
            }], nextMonthAriaLabel: [{
                type: Input
            }], prevYearAriaLabel: [{
                type: Input
            }], nextYearAriaLabel: [{
                type: Input
            }], prevYearRangeAriaLabel: [{
                type: Input
            }], nextYearRangeAriaLabel: [{
                type: Input
            }], closeButtonAriaLabel: [{
                type: Input
            }], weekNumberFormatString: [{
                type: Input
            }] } });

let FabCalendarComponent = class FabCalendarComponent extends ReactWrapperComponent {
    constructor(elementRef, changeDetectorRef, renderer) {
        super(elementRef, changeDetectorRef, renderer);
        this.onSelectDate = new EventEmitter();
        this.onDismiss = new EventEmitter();
        // coming from React context - we need to bind to this so we can access the Angular Component properties
        this.onSelectDateHandler = this.onSelectDateHandler.bind(this);
        this.onDismissHandler = this.onDismissHandler.bind(this);
    }
    ngAfterContentInit() {
        if (this.calendarStringsDirective) {
            this._initDirective(this.calendarStringsDirective);
            super.ngAfterContentInit();
        }
    }
    onSelectDateHandler(date, selectedDateRangeArray) {
        this.onSelectDate.emit({
            date,
            selectedDateRangeArray,
        });
    }
    onDismissHandler() {
        this.onDismiss.emit();
    }
    _initDirective(calendarStringsDirective) {
        this.strings = calendarStringsDirective.strings;
    }
};
/** @nocollapse */ FabCalendarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabCalendarComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ FabCalendarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.1", type: FabCalendarComponent, selector: "fab-calendar", inputs: { componentRef: "componentRef", className: "className", isMonthPickerVisible: "isMonthPickerVisible", isDayPickerVisible: "isDayPickerVisible", showMonthPickerAsOverlay: "showMonthPickerAsOverlay", today: "today", value: "value", firstDayOfWeek: "firstDayOfWeek", dateRangeType: "dateRangeType", showGoToToday: "showGoToToday", strings: "strings", highlightCurrentMonth: "highlightCurrentMonth", highlightSelectedMonth: "highlightSelectedMonth", navigationIcons: "navigationIcons", showWeekNumbers: "showWeekNumbers", firstWeekOfYear: "firstWeekOfYear", dateTimeFormatter: "dateTimeFormatter", minDate: "minDate", maxDate: "maxDate", restrictedDates: "restrictedDates", showSixWeeksByDefault: "showSixWeeksByDefault", workWeekDays: "workWeekDays", showCloseButton: "showCloseButton", allFocusable: "allFocusable" }, outputs: { onSelectDate: "onSelectDate", onDismiss: "onDismiss" }, queries: [{ propertyName: "calendarStringsDirective", first: true, predicate: CalendarStringsDirective, descendants: true, static: true }], viewQueries: [{ propertyName: "reactNodeRef", first: true, predicate: ["reactNode"], descendants: true, static: true }], exportAs: ["fabCalendar"], usesInheritance: true, ngImport: i0, template: `
    <Calendar
      #reactNode
      [componentRef]="componentRef"
      [className]="className"
      [isMonthPickerVisible]="isMonthPickerVisible"
      [isDayPickerVisible]="isDayPickerVisible"
      [showMonthPickerAsOverlay]="showMonthPickerAsOverlay"
      [today]="today"
      [value]="value"
      [firstDayOfWeek]="firstDayOfWeek"
      [dateRangeType]="dateRangeType"
      [showGoToToday]="showGoToToday"
      [strings]="strings"
      [highlightCurrentMonth]="highlightCurrentMonth"
      [highlightSelectedMonth]="highlightSelectedMonth"
      [navigationIcons]="navigationIcons"
      [showWeekNumbers]="showWeekNumbers"
      [firstWeekOfYear]="firstWeekOfYear"
      [dateTimeFormatter]="dateTimeFormatter"
      [minDate]="minDate"
      [maxDate]="maxDate"
      [maxDate]="restrictedDates"
      [restrictedDates]="restrictedDates"
      [showSixWeeksByDefault]="showSixWeeksByDefault"
      [workWeekDays]="workWeekDays"
      [showCloseButton]="showCloseButton"
      [allFocusable]="allFocusable"
      [SelectDate]="onSelectDateHandler"
      [Dismiss]="onDismissHandler"
    >
    </Calendar>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
FabCalendarComponent = __decorate([
    AngularReact(),
    Styled('FabCalendarComponent')
], FabCalendarComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabCalendarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fab-calendar',
                    exportAs: 'fabCalendar',
                    template: `
    <Calendar
      #reactNode
      [componentRef]="componentRef"
      [className]="className"
      [isMonthPickerVisible]="isMonthPickerVisible"
      [isDayPickerVisible]="isDayPickerVisible"
      [showMonthPickerAsOverlay]="showMonthPickerAsOverlay"
      [today]="today"
      [value]="value"
      [firstDayOfWeek]="firstDayOfWeek"
      [dateRangeType]="dateRangeType"
      [showGoToToday]="showGoToToday"
      [strings]="strings"
      [highlightCurrentMonth]="highlightCurrentMonth"
      [highlightSelectedMonth]="highlightSelectedMonth"
      [navigationIcons]="navigationIcons"
      [showWeekNumbers]="showWeekNumbers"
      [firstWeekOfYear]="firstWeekOfYear"
      [dateTimeFormatter]="dateTimeFormatter"
      [minDate]="minDate"
      [maxDate]="maxDate"
      [maxDate]="restrictedDates"
      [restrictedDates]="restrictedDates"
      [showSixWeeksByDefault]="showSixWeeksByDefault"
      [workWeekDays]="workWeekDays"
      [showCloseButton]="showCloseButton"
      [allFocusable]="allFocusable"
      [SelectDate]="onSelectDateHandler"
      [Dismiss]="onDismissHandler"
    >
    </Calendar>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }]; }, propDecorators: { reactNodeRef: [{
                type: ViewChild,
                args: ['reactNode', { static: true }]
            }], componentRef: [{
                type: Input
            }], className: [{
                type: Input
            }], isMonthPickerVisible: [{
                type: Input
            }], isDayPickerVisible: [{
                type: Input
            }], showMonthPickerAsOverlay: [{
                type: Input
            }], today: [{
                type: Input
            }], value: [{
                type: Input
            }], firstDayOfWeek: [{
                type: Input
            }], dateRangeType: [{
                type: Input
            }], showGoToToday: [{
                type: Input
            }], strings: [{
                type: Input
            }], highlightCurrentMonth: [{
                type: Input
            }], highlightSelectedMonth: [{
                type: Input
            }], navigationIcons: [{
                type: Input
            }], showWeekNumbers: [{
                type: Input
            }], firstWeekOfYear: [{
                type: Input
            }], dateTimeFormatter: [{
                type: Input
            }], minDate: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], restrictedDates: [{
                type: Input
            }], showSixWeeksByDefault: [{
                type: Input
            }], workWeekDays: [{
                type: Input
            }], showCloseButton: [{
                type: Input
            }], allFocusable: [{
                type: Input
            }], onSelectDate: [{
                type: Output
            }], onDismiss: [{
                type: Output
            }], calendarStringsDirective: [{
                type: ContentChild,
                args: [CalendarStringsDirective, { static: true }]
            }] } });

// Copyright (c) Microsoft Corporation. All rights reserved.
// Dummy action to force CalendarCss to load and not be tree-shaken away.
// noop(CalendarCss);
const declarations = [FabCalendarComponent, CalendarStringsDirective];
class FabCalendarModule {
    constructor() {
        // Add any React elements to the registry (used by the renderer).
        registerElement('Calendar', () => Calendar);
    }
}
/** @nocollapse */ FabCalendarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabCalendarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ FabCalendarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabCalendarModule, declarations: [FabCalendarComponent, CalendarStringsDirective], imports: [CommonModule], exports: [FabCalendarComponent, CalendarStringsDirective] });
/** @nocollapse */ FabCalendarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabCalendarModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabCalendarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: declarations,
                    exports: declarations,
                    schemas: [NO_ERRORS_SCHEMA],
                }]
        }], ctorParameters: function () { return []; } });

// Copyright (c) Microsoft Corporation. All rights reserved.

/**
 * Generated bundle index. Do not edit.
 */

export { CalendarStringsDirective, FabCalendarComponent, FabCalendarModule };
